import { DIALER_STATES } from "@/constants/dialer";
import {
  DefaultCallingContextI,
  InCallContextI,
} from "@/interfaces/contexts/calling";
import { Call, Device } from "@twilio/voice-sdk";

import { toast } from "react-hot-toast";
import { DIALER_LIVE_TRANSFER_STATUSES } from "@/constants/dialer";

import {
  CallingService,
  StartCallResponseI,
  TwilioAccessTokenResponseI,
  glenSocketClientAPI,
} from "@/api/glen-socket";
import {
  GetAccountCalendlyUriResponseI,
  glencocoClientAPI,
} from "@/api/glencoco";

import { defaultLeadInfo } from "@/context/dialer-v1/default-context";

import dayjs from "dayjs";
import { FrequentlyChangingCallingContextEventDispatcher } from "@/context/dialer-v1/frequently-changing";
import { handleErrorMessage } from "./error";
import { AxiosResponse } from "axios";
import { DefaultResponseI } from "shared/lib/interfaces/api";
import { dd } from "./datadog";
import { asyncGet, asyncWaitFor } from "./context";
import { DialerGlobalContextI } from "@/context/dialer-global";
import {
  isDialerWidgetParamsEqualConnectedLead,
  isDialerWidgetOpen,
  isDialerWidgetUrlParamsSet,
} from "./widgets";
import wait from "wait";
import { ERROR_CATEGORIES } from "@/constants/errors";
import { LOG_CATEGORIES } from "@/constants/logs";
import { SessionStorage } from "./session-storage";

import { DayJs } from "shared/lib/helpers/date";
import { TwilioSetupConfigI } from "@/interfaces/dialer/twilio";
import { TWILIO_TOKEN_ENDPOINT_VERSIONS } from "@/constants/twilio";
import { GLOBAL_DIALER_STATE } from "@/constants/window-globals";

import { dispatchCustomEvent } from "./events";
import { CUSTOM_EVENTS } from "@/constants/custom-events";
import { CustomEventData_ListDialing_ConnectedLeadI } from "@/interfaces/events/list-dialing";
import { GetCallerPhoneNumberByCallIdRsponseI } from "@/api/routes/dialer";
import { GetUserAccountDetailsResponseI } from "@/api/routes/account/interfaces";
import { convertAccountToCallAccount } from "./account";
import { convertContactDetailToContact } from "./contact";
import { modalHelpers } from "shared/lib/helpers/modalHelpers";
import { MICROPHONE_ACCESS_MODAL_ID } from "@/components/modals/microphone-access-modal";

const validateCallCustomParams = (call: Call): boolean => {
  const callId = call?.customParameters.get("call_id") as string;
  const membershipId = call?.customParameters.get("membership_id") as string;

  if (!callId || !membershipId) {
    dd.rum.error(
      `${ERROR_CATEGORIES.DIALER_LIST} - required call custom params are undefined`,
      {
        data: Object.fromEntries(call?.customParameters || []),
      }
    );

    return false;
  }

  return true;
};

const dialerGlobalProvider_UpdateConnectedContact = async ({
  call,
  defaultCallContext,
  inCallContext,
  dialerGlobalContext,
}: {
  dialerGlobalContext?: DialerGlobalContextI;
  call: Call;
  defaultCallContext: DefaultCallingContextI;
  inCallContext: InCallContextI;
}) => {
  if (!validateCallCustomParams(call)) {
    toast.error(`Failed to retrieve membership id - disconnecting`);
    call.disconnect();
    return;
  }

  const callId = call.customParameters.get("call_id") as string;
  const membershipId = call.customParameters.get("membership_id") as string;
  let campaignId = call.customParameters.get("campaign_id") as string;

  const API = glencocoClientAPI();

  /**
   * NOTE
   *
   * Usually you don't have to wait for campaign id except if it is a global dialer
   * and you get connected before you get campaignId
   *
   * Recent changes
   * CampaignId is not stable indialing list connected lead. As it assingment not blocking
   * incomming message and might be executed here earlier than campaign id update
   */

  if (!campaignId) {
    campaignId = await asyncWaitFor(defaultCallContext.setCampaignID, {
      expectedValue: (v: string) => !!v,
      timeout: 2000,
    });
  }

  const resp = await API.getCallerPhoneNumberByCallId(callId).catch((e) => e);

  if (resp.status !== 200) {
    dd.error(
      `${ERROR_CATEGORIES.DIALER_LIST} - failed to get caller phone number`,
      { data: { callId, campaignId } }
    );
  }

  const data = resp.data as GetCallerPhoneNumberByCallIdRsponseI;

  inCallContext.setConnectedPhone(data.contact_phone);
  inCallContext.setCallerPhone(data.caller_phone);
  inCallContext.setContact({ id: data.contact_id });

  /**
   * NOTE
   *
   * In the list dialing mode trigger the event that lead has been connected
   * and let leads queue manager handle it
   */
  Calling.dialerGlobalProvider_handleDeviceIncomingMessage_SetConnectedLead({
    dialerGlobalContext,
    data: {
      campaignId,
      membershipId,
    },
  });
};

const updateConnectedContact = async ({
  context: defaultCallContext,
  inCallContext,
  dialerGlobalContext,
  call,
  isOutboundCall,
}: {
  context: DefaultCallingContextI;
  inCallContext: InCallContextI;
  dialerGlobalContext?: DialerGlobalContextI;
  call: Call;
  isOutboundCall: boolean;
}) => {
  /**
   * Deviation from using call context in list dialing mode
   */
  if (dialerGlobalContext) {
    const isGlobalDialerEnabled = await asyncGet(
      dialerGlobalContext.setIsEnabled
    );

    if (isGlobalDialerEnabled) {
      dialerGlobalProvider_UpdateConnectedContact({
        call,
        defaultCallContext,
        inCallContext,
        dialerGlobalContext,
      });

      return;
    }
  }

  /**
   * All other calls like auto/targeted, direct calls
   */

  const callId = call.customParameters.get("call_id") as string;
  let campaignId = call.customParameters.get("campaign_id") as string;
  const API = glencocoClientAPI();

  /**
   * NOTE
   *
   * Usually you don't have to wait for campaign id except if it is a global dialer
   * and you get connected before you get campaignId
   *
   * Recent changes
   * CampaignId is not stable indialing list connected lead. As it assingment not blocking
   * incomming message and might be executed here earlier than campaign id update
   */

  if (!campaignId) {
    campaignId = await asyncWaitFor(defaultCallContext.setCampaignID, {
      expectedValue: (v: string) => !!v,
      timeout: 2000,
    });
  }
  /**
   * Temp code as a fast replacement of call context
   */
  const Resp = await API.getCallerPhoneNumberByCallId(callId).catch((e) => e);

  if (Resp.status !== 200) {
    dd.error(
      `${ERROR_CATEGORIES.DIALER_LIST} - failed to get caller phone number`,
      { data: { callId, campaignId } }
    );

    return;
  }

  const data = Resp.data as GetCallerPhoneNumberByCallIdRsponseI;

  inCallContext.setConnectedPhone(data.contact_phone);
  inCallContext.setCallerPhone(data.caller_phone);
  inCallContext.setContact({ id: data.contact_id });

  const RespAccountDetails = await API.getAccountDetails(
    campaignId as string,
    data?.account_id as string
  ).catch((e) => e);

  if (RespAccountDetails.status !== 200) {
    dd.rum.error(`${ERROR_CATEGORIES.TWILIO} - failed to update call context`, {
      data: {
        response: RespAccountDetails,
        requestParams: { campaignId, callId },
        call,
        callCustomParams: Array.from(
          call?.customParameters,
          ([key, value]) => ({
            key,
            value,
          })
        ),
      },
    });
    return;
  }

  const accountDetails =
    RespAccountDetails?.data as GetUserAccountDetailsResponseI;

  const account = accountDetails?.account;
  const contact = accountDetails?.contacts?.find(
    (c) => c.id === data?.contact_id
  );

  inCallContext.setAccount(convertAccountToCallAccount(account));
  inCallContext.setContact(convertContactDetailToContact(contact));

  const RespCalendly = await API.getAccountCalendlyUri(
    data?.account_id,
    campaignId
  ).catch((e) => e);

  if (RespCalendly.status === 200) {
    // oxlint-disable-next-line no-unsafe-optional-chaining -- Pre-existing. A fix changes the error path, so it needs its own change.
    const calendly_uri = (RespCalendly?.data as GetAccountCalendlyUriResponseI)
      .uri;
    inCallContext.setCalendlyUri(calendly_uri);
  }

  /**
   * Activity log is outdated and should be refactored
   */
  // const activity_log = accountDetails;
  // inCallContext.setActivityLog(
  //   activity_log?.length ? activity_log?.reverse() : []
  // );

  //TODO remove notfications from here completely

  if (isOutboundCall) {
    toast.success(`Calling ${contact?.first_name} ${contact?.last_name}...`);
    return;
  }

  /**
   * In Account Details sidebar we don't want this notification
   */
  const successNotification = `You're connected with ${contact?.first_name} ${contact?.last_name}`;

  if (defaultCallContext) {
    toast.success(successNotification);
  } else {
    toast.success(successNotification);
  }
};

const Calling = {
  async setAudioDevices(context: DefaultCallingContextI, device: Device) {
    await wait(500);

    const inputDevices = Array.from(device?.audio?.availableInputDevices ?? []);

    context.setInputDevices(inputDevices);

    if (inputDevices[0]) {
      device?.audio?.setInputDevice(inputDevices[0][0]);
    }

    context.setOutputDevices(
      Array.from(device.audio?.availableOutputDevices ?? [])
    );
  },

  async startCallAndEnqueueCaller(
    context: DefaultCallingContextI
  ): Promise<boolean> {
    if (!context.campaignId) {
      toast.error(`Unknown Campaign`);
      return false;
    }

    await Calling.safeUpdateTwilioAccessToken(context);

    const API = glenSocketClientAPI();
    const startCallResp = await API.startCalls(context.campaignId).catch(
      (e) => {
        return e;
      }
    );

    let success = false;
    if (startCallResp?.status === 200) {
      success = true;
    } else if (startCallResp?.status === 202) {
      toast(`No leads to call right now. Please try again later`);
      success = false;
    } else {
      handleErrorMessage(
        startCallResp?.response?.data?.error_code as number,
        `Failed to start call`
      );

      success = false;
    }

    if (success) {
      context.setCallingState(DIALER_STATES.DIALING);
      // Register device again to receive call
      await Calling.safeUnregisterDevice(context);
      await Calling.safeRegisterDevice(context);
      toast(`Dialing leads...`);
    }

    return success;
  },

  async safeUpdateTwilioAccessToken(
    context: DefaultCallingContextI,
    config?: TwilioSetupConfigI,
    onSuccess?: (accessTokenResponseData: TwilioAccessTokenResponseI) => void
  ) {
    const campaignId = await asyncGet(context.setCampaignID);

    if (!campaignId) {
      toast.error(`Unknown Campaign`);
      return;
    }

    const { setDevice } = context;
    const currentDevice = await asyncGet(setDevice);

    if (!currentDevice) {
      console.error(`Device not found. Can't update Twilio access token.`);
      return;
    }

    await Calling.updateTwilioAccessToken(currentDevice, config, onSuccess);
  },

  async updateTwilioAccessToken(
    device: Device,
    config?: TwilioSetupConfigI,
    onSuccess?: (accessTokenResponseData: TwilioAccessTokenResponseI) => void
  ) {
    const API = glenSocketClientAPI();

    const getTwilioAccessToken =
      config?.tokenEndpointVersion === TWILIO_TOKEN_ENDPOINT_VERSIONS.V3
        ? API.getTwilioAccessTokenV3
        : API.getTwilioAccessToken;

    const accessTokenResponse = await getTwilioAccessToken().catch((e) => {
      dd.error(`Failed to get new Twilio access token`);
      toast.error(
        `Failed to get refresh calling session. Please refresh the page`
      );
      return e;
    });

    if (accessTokenResponse.status === 200) {
      const SS = new SessionStorage();
      device.updateToken(accessTokenResponse.data.token);

      SS.dialerGlobalTwilioTokenUpdatedAt = new Date().toISOString();

      onSuccess?.(accessTokenResponse.data);
    }
  },

  async isDeviceRegistered(context: DefaultCallingContextI) {
    const { setDevice } = context;
    const currentDevice = await asyncGet(setDevice);

    return currentDevice?.state === Device.State.Registered;
  },

  async safeUnregisterDevice(context: DefaultCallingContextI) {
    const { setDevice } = context;
    const currentDevice = await asyncGet(setDevice);
    if (currentDevice) {
      await currentDevice.unregister().catch((e) => {
        if (e.name === "InvalidStateError") {
          console.log(`Twilio device already unregistered`);
        } else {
          dd.log(`${LOG_CATEGORIES.TWILIO} error currentDevice.unregister()`, {
            data: { e },
          });
        }
      });
    }
  },

  async safeRegisterDevice(context: DefaultCallingContextI) {
    const { setDevice } = context;
    const currentDevice = await asyncGet(setDevice);
    if (currentDevice) {
      await currentDevice.register().catch((e) => {
        if (e.name === "InvalidStateError") {
          console.log(`Twilio device already registered`);
        } else {
          dd.log(`${LOG_CATEGORIES.TWILIO} error currentDevice.register()`, {
            data: { e },
          });
        }
      });

      await Calling.setAudioDevices(context, currentDevice);
    }
  },

  /**
   * NOTE: transfer current call to another caller
   *       should be available only for specific
   *       campaigns that allow live transfer.
   * @param {Object} props
   * @param props.context - CallingContext
   * @param props.inCallContext - InCallContext
   * @returns {Promise} Promise as the reponse of API call for the live transfer
   */
  async liveTransfer({
    context,
    inCallContext,
  }: {
    context: DefaultCallingContextI;
    inCallContext: InCallContextI;
  }) {
    const { setCall } = context;
    const currentCall = await asyncGet(setCall);

    if (!currentCall) {
      toast.error(`No call to transfer`);
      return;
    }

    const { isCallOnDemand, callOnDemandConfig } = context;
    const callId =
      isCallOnDemand && callOnDemandConfig?.callId
        ? (callOnDemandConfig?.callId as string)
        : (currentCall?.customParameters.get("call_id") as string);

    const API = glenSocketClientAPI();

    if (!callId) {
      toast.error(`Unknown call id`);
      return;
    }

    const LiveTransferResponse = await API.startLiveTransfer(
      context.campaignId as string,
      {
        call_id: callId,
      }
    ).catch((e) => {
      toast.error(`Failed to start transfer`);
      return e;
    });

    if (LiveTransferResponse?.status === 200) {
      toast.success(`Transfer started`);
      inCallContext.setLiveTransferStatus(
        DIALER_LIVE_TRANSFER_STATUSES.LIVE_TRANSFER_COMPLETE
      );
    } else {
      handleErrorMessage(
        LiveTransferResponse?.response?.data?.error_code as number,
        `Failed to start transfer`
      );
    }

    return LiveTransferResponse as AxiosResponse<DefaultResponseI>;
  },

  async startCall({
    context,
    inCallContext,
    dialerGlobalContext,
    contactId,
    itemId,
    phone,
  }: {
    context: DefaultCallingContextI;
    inCallContext: InCallContextI;
    dialerGlobalContext?: DialerGlobalContextI;
    contactId?: string;
    itemId?: string;
    phone?: string;
  }) {
    const campaignId = await asyncGet(context.setCampaignID);

    if (!campaignId) {
      dd.rum.error(
        `${ERROR_CATEGORIES.TWILIO} - failed to start the call - unknown campaign`,
        {
          data: {
            contactId,
            itemId,
          },
        }
      );

      toast.error(`Unknown Campaign`);
      return false;
    }

    const API = glenSocketClientAPI();

    if (!dialerGlobalContext)
      await Calling.safeUpdateTwilioAccessToken(context);

    if (dialerGlobalContext) {
      const isGlobalDialerEnabled = await asyncGet(
        dialerGlobalContext.setIsEnabled
      );

      if (isGlobalDialerEnabled) {
        await Calling.safeUpdateTwilioAccessToken(
          context,
          {
            tokenEndpointVersion: "v3",
          },
          (accessTokenResponseData) => {
            dialerGlobalContext.setTwilioIdentity(
              accessTokenResponseData?.identity
            );
          }
        );
      }
    }

    const resp = await API.startDirectCall(campaignId, {
      contact_id: contactId,
      item_id: itemId,
      phone,
    }).catch((e) => {
      toast.error(`Failed to start call`);

      if (context.isCallOnDemand) {
        context.setCallingState(DIALER_STATES.PRE);
      }
      Calling.safeUnregisterDevice(context);
      return e;
    });

    if (resp?.status === 200) {
      const params = {
        // get the phone number to call from the DOM
        Type: "direct-call",
        CallID: (resp?.data as StartCallResponseI)?.call_id,
        To: (resp?.data as StartCallResponseI)?.to_phone_number,
      };

      context.callOnDemandConfig.setCallId(
        (resp?.data as StartCallResponseI)?.call_id
      );

      const { setDevice } = context;
      const device = await asyncGet(setDevice);
      if (device) {
        await Calling.safeUnregisterDevice(context);
        await Calling.safeRegisterDevice(context);

        device.disconnectAll();

        const call = await device.connect({ params });
        Calling.addCallListeners(
          {
            context,
            inCallContext,
          },
          call
        );

        call.customParameters.set(
          "call_id",
          (resp?.data as StartCallResponseI)?.call_id
        );
        await Calling.acceptCall({ context, inCallContext }, call, true);
        context.setCallingState(DIALER_STATES.CALL);
      } else {
        toast.error(`Failed to connect Twilio device`);
        context.setCallingState(DIALER_STATES.PRE);
        Calling.safeUnregisterDevice(context);
      }
    } else {
      handleErrorMessage(
        resp?.response?.data?.error_code as number,
        "Failed to start call"
      );

      if (context.isCallOnDemand) {
        context.setCallingState(DIALER_STATES.PRE);
      }

      Calling.safeUnregisterDevice(context);
    }
  },

  async acceptCall(
    {
      context,
      inCallContext,
      dialerGlobalContext,
    }: {
      context: DefaultCallingContextI;
      inCallContext: InCallContextI;
      dialerGlobalContext?: DialerGlobalContextI;
    },
    call: Call,
    isOutboundCall = false
  ) {
    const { setCall, setMuted } = context;

    context.setLeadInfo(defaultLeadInfo);
    context.setLeadQueue([]);

    setCall(call);

    call.accept();
    dd.rum.log(`${LOG_CATEGORIES.TWILIO} - Accepted Call`);

    Calling.dialerGlobalProvider_debugCallAcceptanceLatency({
      dialerGlobalContext,
    });

    const isMute = await asyncGet(setMuted);

    setTimeout(() => {
      call?.mute(isMute);
    }, 200);

    if (!call.customParameters.has("call_id")) {
      dd.rum.error(`${ERROR_CATEGORIES.TWILIO} - no call id`, call);

      return;
    }

    await updateConnectedContact({
      context,
      inCallContext,
      dialerGlobalContext,
      call,
      isOutboundCall,
    });
  },

  async callEnded({
    context,
    inCallContext,
  }: {
    context: DefaultCallingContextI;
    inCallContext?: InCallContextI;
  }) {
    Calling.safeUnregisterDevice(context);

    if (inCallContext) {
      const endAt = dayjs().format();

      inCallContext.setEndAt(endAt);

      let startAt = await asyncGet(inCallContext?.setStartAt);
      const call = await asyncGet(context?.setCall);

      const twilioCallStartAt = call?.parameters?.start_time;
      startAt = startAt || (twilioCallStartAt as string);

      if (!endAt || !startAt) {
        dd.rum.log(
          `${ERROR_CATEGORIES.TWILIO}[CALL DURATION] - failed to measure call length`,
          { data: { startAt, endAt } }
        );
      }

      if (
        endAt &&
        startAt &&
        DayJs(endAt).diff(DayJs(startAt), "seconds") < 2
      ) {
        dd.rum.error(
          `${ERROR_CATEGORIES.TWILIO}[CALL DURATION] - Too short (less than 2 seconds)`,
          { data: { defaultContext: context, inCallContext: inCallContext } }
        );
      }
    }

    context.setCallingState(DIALER_STATES.DISPOSITION);
  },

  async callNextContactOrBackToLeadSelection({
    context,
    inCallContext,
  }: {
    context: DefaultCallingContextI;
    inCallContext: InCallContextI;
  }) {
    const {
      setLeadQueue,
      callerProfile: { setAutoDial },
    } = context;

    const currentQueue = await asyncGet(setLeadQueue);

    if (currentQueue.length) {
      const nextContact = currentQueue[0];

      Calling.startCall({
        context,
        inCallContext,
        contactId: nextContact.id,
      });
    } else {
      const autoDial = await asyncGet(setAutoDial);

      if (autoDial) {
        toast("Finding next contacts...");
        setTimeout(async () => {
          const success = await Calling.startCallAndEnqueueCaller(context);
          if (!success) {
            context.setCallingState(DIALER_STATES.PRE);
          }
        }, 1000);
        context.setCallingState(DIALER_STATES.POST);
      } else {
        setTimeout(() => {
          if (context.isCallOnDemand) {
            context.setCallingState(DIALER_STATES.PRE);
          } else {
            context.setCallingState(DIALER_STATES.LEAD_SELECTION);
          }
        }, 2000);
        context.setCallingState(DIALER_STATES.POST);
        Calling.safeUnregisterDevice(context);
      }
    }
  },

  async clearCallData({
    context,
    inCallContext,
  }: {
    context: DefaultCallingContextI;
    inCallContext?: InCallContextI;
  }) {
    context.setCall(undefined);
    context.setIsCallOnDemand(false);
    context.callOnDemandConfig?.setCallId("");

    inCallContext?.clear();

    await Calling.safeUpdateTwilioAccessToken(context);
  },

  /**
   * Handles incoming messages and sets the connected lead in the dialer global context.
   * This function checks if `dialerGlobalContext` is provided, and if so, it dispatches
   * a custom event to indicate that a lead has been connected.
   *
   * @async
   * @function dialerGlobalProvider_handleDeviceIncomingMessage_SetConnectedLead
   * @param {Object} params - The function parameters.
   * @param {DialerGlobalContextI} [params.dialerGlobalContext] - The global context for the dialer.
   * @param {CustomEventData_ListDialing_ConnectedLeadI} params.data - The data for the incoming message, including campaign and contact information.
   * @returns {Promise<void>} Returns nothing if `dialerGlobalContext` is not provided.
   */
  async dialerGlobalProvider_handleDeviceIncomingMessage_SetConnectedLead({
    dialerGlobalContext,
    data,
  }: {
    dialerGlobalContext?: DialerGlobalContextI;
    data: CustomEventData_ListDialing_ConnectedLeadI;
  }) {
    /**
     * Further is only when related to the dialerGlobalContext
     */
    if (!dialerGlobalContext) {
      return;
    }

    dispatchCustomEvent({
      eventType: CUSTOM_EVENTS.LIST_DIALING.LEAD_CONNECTED,
      data,
    });
  },

  /**
   *
   * Should be called when Global Dialer is active
   *
   * Multiple Calls can be connected during active session
   *
   * Is user
   *  - currently have an active call
   *  - or dialer widget is open (Filling the disposition)
   *
   * no other call should be connected even if successfully routed
   *
   * @param props
   * @param {DialerGlobalContextI} props.dialerGlobalContext
   *
   * @returns {boolean}
   */
  async handleDeviceIncomingMessage_shouldRejectCall({
    callingContext,
    dialerGlobalContext,
  }: {
    callingContext: DefaultCallingContextI;
    dialerGlobalContext?: DialerGlobalContextI;
  }) {
    /**
     * If current device is busy with a call
     */
    const twDevice = await asyncGet(callingContext.setDevice);
    if (twDevice?.isBusy) {
      dd.log(
        `${LOG_CATEGORIES.DIALER_LIST} - Rejection reason - device is busy`
      );
      return true;
    }

    /**
     * Further is only when related to the dialerGlobalContext
     */
    if (!dialerGlobalContext) {
      return false;
    }

    const isEnabled = await asyncGet(dialerGlobalContext.setIsEnabled);
    const isSessionClosing = await asyncGet(
      dialerGlobalContext.setIsSessionClosing
    );
    /**
     * Trying to accept the call either after session has ended
     * OR session is in the processes of wrapping up
     */
    if (!isEnabled || isSessionClosing) {
      dd.log(
        `${LOG_CATEGORIES.DIALER_LIST} - Rejection reason - Session is Closing`
      );
      return true;
    }

    /**
     * If Dialer Widget is open and user hasn't finished filling disposition
     *
     * There is a split second case when
     * - user is connected
     * - active widget has dialer as triggered
     * - url params for dialer is not set
     * - so they are not equal with the connected lead
     *
     * If isDialerWidgetUrlParamsSet === false - means that it is not set yet
     */
    const connectedLead = await asyncGet(dialerGlobalContext?.setConnectedLead);
    if (
      connectedLead &&
      isDialerWidgetOpen() &&
      isDialerWidgetUrlParamsSet() &&
      !isDialerWidgetParamsEqualConnectedLead(connectedLead)
    ) {
      dd.log(
        `${LOG_CATEGORIES.DIALER_LIST} - Rejection reason - widget is open with the different connected lead`,
        { data: (window as any)?.[GLOBAL_DIALER_STATE] }
      );

      return true;
    }

    return false;
  },

  /**
   * NOTE
   *
   * we want to mesure how much time has passed
   * between we have received ws message that call is connected
   * and twilio has fired event incoming
   *
   */
  async dialerGlobalProvider_debugCallAcceptanceLatency({
    dialerGlobalContext,
  }: {
    dialerGlobalContext?: DialerGlobalContextI;
  }) {
    if (!dialerGlobalContext) return;

    const callWSConnectedAt = (window as any).GLOBAL_DEBUG_CALL_CONNECTED_AT;

    if (!callWSConnectedAt) {
      dd.log(
        `${LOG_CATEGORIES.DIALER_LIST} - call acceptance latency ms - Twilio event fired earlier than WS CONNECTED received`,
        {
          data: {
            duration: 0,
          },
        }
      );

      return;
    }

    const diff = DayJs.duration(
      DayJs().diff(DayJs(callWSConnectedAt)),
      "milliseconds"
    ).asMilliseconds();

    //NOTE same WS was fired after Twilio is Connected as you would get really long duration time as it relates to the previous call
    if (diff > 2000) {
      dd.log(
        `${LOG_CATEGORIES.DIALER_LIST} - call acceptance latency ms - Twilio event fired earlier than WS CONNECTED received`,
        {
          data: {
            duration: 0,
            diff,
          },
        }
      );

      return;
    }

    dd.log(
      `${LOG_CATEGORIES.DIALER_LIST} - call acceptance latency ms - WS CONNECTED happened earlier than Twilio event was fired`,
      {
        data: {
          duration: diff,
        },
      }
    );
  },

  // ---------------- M A I N  F U N C T I O N  ---------------- \\
  /**
   *
   * Register and unregister all Twillio device listeners
   *
   * List of events
   *  - error
   *  - tokenWillExpire
   *  - deviceChange
   *  - inputVolume
   *  - incoming
   *
   * @param contexes
   * @param device
   */
  addDeviceListeners(
    {
      context,
      inCallContext,
      dialerGlobalContext,
    }: {
      context: DefaultCallingContextI;
      inCallContext: InCallContextI;
      dialerGlobalContext?: DialerGlobalContextI;
    },
    device: Device
  ) {
    // device.on("registered", function () {
    //   console.log("Twilio device is ready to make and receive calls!");
    //   // TODO update device status field in context
    // });
    // device.on("unregistered", function () {
    //   console.log("Twilio device is ready to make and receive calls!");
    //   // TODO update device status field in context
    // });

    /**
     * NOTE: we can update token ONLY WHEN NOT ON THE CALL
     * otherwise call gets dropped
     */
    const protectedUpdateAccessToken = async () => {
      const { setStartAt, setEndAt } = inCallContext;
      const { setDevice } = context;

      const startAt = await asyncGet(setStartAt);
      const endAt = await asyncGet(setEndAt);
      const device = await asyncGet(setDevice);

      const isDeviceHasCalls = device && !!device?.calls?.length;
      const isCallActive = startAt && !endAt;

      if (
        [isDeviceHasCalls, isCallActive].every(
          (condition) => condition === false
        )
      ) {
        Calling.updateTwilioAccessToken(device as Device);
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    device.on("error", (error) => {
      console.error("DEVICE_EVENT::error::", error);

      if (error.code == 20101 || error.code == 20104 || error.code == 20105) {
        protectedUpdateAccessToken();
      }
    });
    device.on("tokenWillExpire", () => {
      dd.rum.log(
        `${LOG_CATEGORIES.TWILIO} DEVICE_EVENT::tokenWillExpire`,
        device
      );
      protectedUpdateAccessToken();
    });
    device.audio?.on("deviceChange", async () => {
      dd.rum.log(`${LOG_CATEGORIES.TWILIO} DEVICE_EVENT::audio::deviceChange`, {
        data: { device },
      });

      context.setInputDevices(
        Array.from(device?.audio?.availableInputDevices ?? [])
      );

      context.setOutputDevices(
        Array.from(device.audio?.availableOutputDevices ?? [])
      );
    });
    device.audio?.on("inputVolume", (inputVol) => {
      FrequentlyChangingCallingContextEventDispatcher.dispatch.inputVolUpdated(
        inputVol
      );
    });

    device.on("incoming", function (call: Call) {
      dd.rum.log(`${LOG_CATEGORIES.TWILIO} DEVICE_EVENT::incoming`, call);
      console.log("DEVICE_EVENT::incoming::", call);

      (async () => {
        const shouldRejectCall =
          await Calling.handleDeviceIncomingMessage_shouldRejectCall({
            callingContext: context,
            dialerGlobalContext,
          });

        if (shouldRejectCall) {
          dd.rum.log(`[TWILIO][INCOMING] - Rejected call`, { data: { call } });

          call?.reject();

          return;
        }

        Calling.acceptCall(
          { context, inCallContext, dialerGlobalContext },
          call
        );

        const startAt = dayjs().format();

        inCallContext.setStartAt(startAt);

        Calling.addCallListeners(
          {
            context,
            inCallContext,
          },
          call
        );

        return context.setCallingState(DIALER_STATES.CALL);
      })();
    });
  },

  addCallListeners(
    {
      context,
      inCallContext,
    }: {
      context: DefaultCallingContextI;
      inCallContext: InCallContextI;
    },
    call: Call
  ) {
    // Add event listeners to call object
    const callEnded = () => {
      (async () => {
        await Calling.callEnded({ context, inCallContext });
      })();
    };

    call.on("accept", () => {
      console.log("DEVICE_EVENT::accept");
    });

    call.on("messageReceived", (message) => {
      //NOTE the voiceEventSid can be used for tracking the message
      dd.log(`${LOG_CATEGORIES.TWILIO}[MESSAGE_RECEIVED] - event handler`, {
        data: { message },
      });

      // NOTE - custom property provided by the BE to track
      //        when the call actually started.
      const status = message.content?.status;
      const callId = message.content?.call_id;
      const campaignId = message.content?.campaign_id;
      const membershipId = message.content?.membership_id;
      const contactPhone = message.content?.contact_phone;
      const callerPhone = message.content?.caller_phone;
      const contactId = message.content?.contact_id;

      if (status === "answered") {
        const startAt = dayjs().format();
        inCallContext.setStartAt(startAt);
      }

      if (callId) {
        call.customParameters.set("call_id", callId);
      }

      if (campaignId) {
        call.customParameters.set("campaign_id", campaignId);
      }

      if (membershipId) {
        call.customParameters.set("membership_id", membershipId);
      }

      if (contactPhone) {
        inCallContext.setConnectedPhone(contactPhone);
      }

      if (callerPhone) {
        inCallContext.setCallerPhone(callerPhone);
      }

      if (contactId) {
        inCallContext.setContact({ id: contactId });
      }

      /**
       * Dispatch event to let the dialer know that the call has been answered
       * and the lead has been connected for list dialing types
       */
      if (campaignId && membershipId) {
        context.setCall(call);

        dispatchCustomEvent({
          eventType: CUSTOM_EVENTS.LIST_DIALING.LEAD_CONNECTED,
          data: {
            campaignId,
            membershipId,
          },
        });
      }
    });

    call.on("cancel", () => {
      dd.rum.log(`${LOG_CATEGORIES.TWILIO} DEVICE_EVENT::cancel`);
      callEnded();
    });
    call.on("disconnect", () => {
      dd.rum.log(`${LOG_CATEGORIES.TWILIO} DEVICE_EVENT::disconnect`);
      // inCallContext.setIsDisconnected(true);

      callEnded();
    });
    call.on("reject", () => {
      dd.rum.log(`${LOG_CATEGORIES.TWILIO} DEVICE_EVENT::reject`);
      callEnded();
    });
    call.on("volume", (inputVol, outputVol) => {
      FrequentlyChangingCallingContextEventDispatcher.dispatch.inputVolUpdated(
        inputVol
      );
      FrequentlyChangingCallingContextEventDispatcher.dispatch.outputVolUpdated(
        outputVol
      );
    });
    call.on("error", () => {
      // TODO (calling) handle errors in https://www.twilio.com/docs/voice/insights/call-quality-events-twilio-client-sdk#error-and-warning-events
    });
    call.on("warning", (warningName, warningData) => {
      // https://www.twilio.com/docs/voice/insights/call-quality-events-twilio-client-sdk#error-and-warning-events
      switch (warningName) {
        case "low-mos":
          toast.error("Call quality is low");

          // 1 = Bad ~ 5 = Excellent
          FrequentlyChangingCallingContextEventDispatcher.dispatch.callQualityUpdated(
            warningData.values.reduce((a: number, b: number) => a + b) /
              warningData.values.length
          );
          break;
        case "error":
          toast.error("Connection failed");
          break;
        case "failed":
          toast.error("Microphone not accessible");
          break;
        case "denied":
          toast.error("Audio device access denied");
          break;
        default:
      }
    });
    call.on("warning-cleared", (warningName) => {
      switch (warningName) {
        case "low-mos":
          FrequentlyChangingCallingContextEventDispatcher.dispatch.callQualityUpdated(
            5
          );

          break;
        default:
      }
    });
  },

  socketMessageHandler(msg: any) {
    console.log(msg);
  },

  // TODO Refactor logic to handle it in more expected manner
  // TODO callNextContactOrBackToLeadSelection shouldn't be a part of this function
  async exit({
    context,
    inCallContext,
  }: {
    context: DefaultCallingContextI;
    inCallContext: InCallContextI;
  }) {
    if (context.isCallOnDemand) {
      // NOTE
      // Remove activity event id from url
      // so if user reloads the page the previous call won't start
      const url = new URL(window.location.href);
      const pathname = url.pathname?.split("?")[0];
      const splittedPathname = pathname.split("/");

      if (splittedPathname.length === 4) {
        splittedPathname.pop();
        const newURL = `${url.origin}${splittedPathname.join("/")}`;

        history?.replaceState({}, document.title, newURL);
      }
    }

    context.setCallingState(DIALER_STATES.POST);

    Calling.clearCallData({ context, inCallContext });
    Calling.callNextContactOrBackToLeadSelection({
      context,
      inCallContext,
    });
  },

  async cleanupAndExit({
    context,
    inCallContext,
  }: {
    context: DefaultCallingContextI;
    inCallContext: InCallContextI;
  }) {
    if (context.isCallOnDemand) {
      // NOTE
      // Remove activity event id from url
      // so if user reloads the page the previous call won't start
      const url = new URL(window.location.href);
      const pathname = url.pathname?.split("?")[0];
      const splittedPathname = pathname.split("/");

      if (splittedPathname.length === 4) {
        splittedPathname.pop();
        const newURL = `${url.origin}${splittedPathname.join("/")}`;

        history?.replaceState({}, document.title, newURL);
      }
    }

    Calling.clearCallData({ context, inCallContext });

    context.setCallingState(DIALER_STATES.PRE);
  },
};

export const getWebSocketConnection = (callingCtx: DefaultCallingContextI) => {
  const API = glenSocketClientAPI();

  // Create Websocket connection
  const newWS = API.getWebSocketConnection(
    // on error
    (err) => {
      console.log(`Connection error: ${err}`);
    },
    // on close
    () => {
      console.log("Connection with the backend closed");
    },
    // on open
    async (ws: WebSocket) => {
      if (callingCtx?.campaignId) {
        CallingService(ws).setup(callingCtx?.campaignId);
      }

      callingCtx.socket = ws;
    },
    (msg) => Calling.socketMessageHandler(msg)
  );

  if (newWS === null) {
    toast.error(`Failed to create connection`);
    return;
  }

  return newWS;
};

export const disconnectMic = () => {
  ((window as any).DIALER_MODULE_AUDIO_STREAM as MediaStream)
    ?.getTracks()
    .forEach((track) => {
      track.stop();
    });

  (window as any).DIALER_MODULE_AUDIO_STREAM = null;
};

export const showMicrophonePermissionModalIfNeeded = async () => {
  let hasMicrophonePermission = false;

  if (navigator.permissions) {
    try {
      const result = await navigator.permissions.query({
        name: "microphone",
      } as unknown as PermissionDescriptor);

      if (result.state === "granted") {
        hasMicrophonePermission = true;
      }
    } catch (e) {
      console.warn("Permissions API query failed", e);
    }
  }

  if (!hasMicrophonePermission) {
    modalHelpers.open(MICROPHONE_ACCESS_MODAL_ID);
  }
};

export const askPermissionMic = async () => {
  await showMicrophonePermissionModalIfNeeded();

  return new Promise((resolve, reject) => {
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        disconnectMic();

        (window as any).DIALER_MODULE_AUDIO_STREAM = stream;
        resolve(true);
      })
      .catch(() => {
        reject(false);
      });
  });
};

export const endSession = async (
  callContext: DefaultCallingContextI,
  callbackFn: () => void
) => {
  await Calling.safeUnregisterDevice(callContext);

  const API = glenSocketClientAPI();

  await API.setAutoDial(false).catch((e) => e);

  const { setSocket, setCall, setDevice } = callContext;
  const currentSocket = await asyncGet(setSocket);

  if (currentSocket) {
    CallingService(currentSocket).endSession();
    setSocket(undefined);
  }

  const currentCall = await asyncGet(setCall);
  if (currentCall) {
    currentCall.disconnect();
    dd.rum.log(
      `${LOG_CATEGORIES.TWILIO}[DISCONNECT] - manual disconnect endSession`
    );
    setCall(undefined);
  }

  callContext.setLeadInfo(defaultLeadInfo);

  await Calling.safeUpdateTwilioAccessToken(callContext);
  const currentDevice = await asyncGet(setDevice);
  if (currentDevice) {
    currentDevice.disconnectAll();
  }

  const campaignId = await asyncGet(callContext.setCampaignID);

  if (!campaignId) {
    toast.error(`Unknown Campaign`);
    return;
  }

  API.dequeueCaller(campaignId)
    .then(() => {
      callContext.setLeadInfo(defaultLeadInfo);
      callContext.setLeadQueue([]);
      callbackFn();
    })
    .catch((err) => {
      toast.error("Failed to end session : ", err.message);
    });
};

export default Calling;
